import {
  Box,
  Button,
  Card,
  Flex,
  FormatNumber,
  Heading,
  HStack,
  Icon,
  Image,
  Link,
  Spinner,
  Text,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";
import { FiArrowUpRight } from "react-icons/fi";

import { PointsFaqModal } from "@/components/modals/points-faq";
import { usePPScores } from "@/hooks/usePPScores";
import { formatNumberToLocale } from "@/utils";

export const PPCard = ({ address }: { address: `0x${string}` }) => {
  const { ppScores, isLoading } = usePPScores(address);

  const isDesktop = useBreakpointValue({ base: false, md: true });

  if (isLoading || !ppScores) {
    return <Spinner />;
  }

  if (isDesktop) {
    return (
      <Card.Root
        borderRadius="3xl"
        mb={4}
        mt={6}
        fontFamily="Lufga, sans-serif"
        p={8}
      >
        <Card.Header p={0}>
          <HStack gap={4}>
            <Image src="/assets/profile/pp.svg" boxSize={58} />
            <Box flex={1}>
              <Heading color="fg" size="4xl">
                <FormatNumber value={ppScores.activeXp.totalXp} /> PP
              </Heading>
              <Text color="fg.muted" fontWeight="500" fontFamily="body">
                <Text as="span" fontWeight="700" color="fg">
                  <FormatNumber value={ppScores.activeXp.userSelfXp} />
                </Text>{" "}
                Activity PP |{" "}
                <Text as="span" fontWeight="700" color="fg">
                  <FormatNumber value={ppScores.activeXp.referralBonusXp} />
                </Text>{" "}
                Referral PP
              </Text>
            </Box>
          </HStack>
        </Card.Header>
        <Card.Body p={0}>
          <Flex color="fg.muted" gap={4} pt={8} alignItems="center" w="full">
            <Link
              href="https://discord.gg/plume-network"
              target="_blank"
              rel="noopener noreferrer"
              textDecoration="none"
            >
              <Button variant="outline" rounded="full">
                Discord Support
                <Icon as={FiArrowUpRight} boxSize={4} />
              </Button>
            </Link>
            <PointsFaqModal rounded="full" fontSize="sm" />
          </Flex>
        </Card.Body>
      </Card.Root>
    );
  }

  return (
    <Card.Root rounded="24px" mt={6} mb={3} py={6}>
      <Card.Body p={0}>
        <VStack alignItems="flex-start" gap={4}>
          <Flex gap={4} px={6}>
            <Image src="/assets/profile/pp.svg" boxSize="52px" />
            <Flex
              direction="column"
              gap={1}
              justifyContent="center"
              alignSelf="center"
            >
              <Heading fontSize="2xl" fontWeight="700" lineHeight={1} mb={0}>
                <FormatNumber value={ppScores.activeXp.totalXp} /> PP
              </Heading>
            </Flex>
          </Flex>
          <VStack alignItems="flex-start" gap={1} px={6}>
            <HStack>
              <Text fontSize="md" fontWeight="500" color="text.secondary">
                <Box as="span" color="fg" fontWeight="700">
                  {formatNumberToLocale(ppScores.activeXp.userSelfXp)}
                </Box>{" "}
                Activity PP
              </Text>
              |
              <Text fontSize="md" fontWeight="500" color="text.secondary">
                <Box as="span" color="fg" fontWeight="700">
                  {formatNumberToLocale(ppScores.activeXp.referralBonusXp)}
                </Box>{" "}
                Referral PP
              </Text>
            </HStack>
          </VStack>
          <Box
            borderTopWidth={1}
            borderColor="border"
            borderTopStyle="solid"
            w="full"
          />
          <VStack px={6} w="full">
            <Link
              href="https://discord.gg/plume-network"
              target="_blank"
              rel="noopener noreferrer"
              textDecoration="none"
              w="full"
            >
              <Button
                variant="outline"
                rounded="full"
                w="full"
                fontSize="sm"
                size="lg"
              >
                Discord Support
                <Icon as={FiArrowUpRight} boxSize={4} />
              </Button>
            </Link>
            <PointsFaqModal
              mt={2}
              w="full"
              rounded="full"
              fontSize="sm"
              size="lg"
            />
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  );
};
